<template>
  <div class="auth-page">
    <div class="auth-container">
      <div class="auth-card">
        <div class="auth-header">
          <h1>Reset Your Password</h1>
          <p>Enter your new password below</p>
        </div>

        <div v-if="message" :class="['message', messageType]">
          {{ message }}
        </div>

        <form @submit.prevent="handleSubmit" class="auth-form" v-if="!resetSuccess">
          <div class="form-group">
            <label for="password">New Password</label>
            <div class="password-input">
              <input
                id="password"
                v-model="password"
                :type="showPassword ? 'text' : 'password'"
                placeholder="Enter your new password"
                required
                :disabled="loading"
                minlength="8"
              />
              <button
                type="button"
                class="password-toggle"
                @click="showPassword = !showPassword"
                :disabled="loading"
              >
                <i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
              </button>
            </div>
            <small class="password-hint">Password must be at least 8 characters long</small>
          </div>

          <div class="form-group">
            <label for="confirmPassword">Confirm New Password</label>
            <div class="password-input">
              <input
                id="confirmPassword"
                v-model="confirmPassword"
                :type="showConfirmPassword ? 'text' : 'password'"
                placeholder="Confirm your new password"
                required
                :disabled="loading"
                minlength="8"
              />
              <button
                type="button"
                class="password-toggle"
                @click="showConfirmPassword = !showConfirmPassword"
                :disabled="loading"
              >
                <i :class="showConfirmPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
              </button>
            </div>
          </div>

          <button type="submit" class="auth-button" :disabled="loading || !isFormValid">
            <span v-if="loading" class="loading-spinner"></span>
            {{ loading ? 'Resetting...' : 'Reset Password' }}
          </button>
        </form>

        <div v-if="resetSuccess" class="success-content">
          <div class="success-icon">
            <i class="fas fa-check-circle"></i>
          </div>
          <h2>Password Reset Successfully!</h2>
          <p>Your password has been reset. You can now log in with your new password.</p>
          <router-link to="/login" class="auth-button">
            Go to Login
          </router-link>
        </div>

        <div class="auth-footer" v-if="!resetSuccess">
          <p>
            Remember your password? 
            <router-link to="/login" class="auth-link">Back to Login</router-link>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { apiService } from '@/services/api.js'

export default {
  name: 'ResetPassword',
  setup() {
    const router = useRouter()
    const route = useRoute()
    const password = ref('')
    const confirmPassword = ref('')
    const showPassword = ref(false)
    const showConfirmPassword = ref(false)
    const loading = ref(false)
    const message = ref('')
    const messageType = ref('')
    const resetSuccess = ref(false)
    const token = ref('')

    const isFormValid = computed(() => {
      return password.value.length >= 8 && 
             password.value === confirmPassword.value
    })

    const showMessage = (text, type = 'info') => {
      message.value = text
      messageType.value = type
      setTimeout(() => {
        message.value = ''
        messageType.value = ''
      }, 5000)
    }

    const handleSubmit = async () => {
      if (!isFormValid.value) {
        if (password.value.length < 8) {
          showMessage('Password must be at least 8 characters long', 'error')
        } else if (password.value !== confirmPassword.value) {
          showMessage('Passwords do not match', 'error')
        }
        return
      }

      loading.value = true
      
      try {
        const response = await apiService.resetPassword({
          token: token.value,
          newPassword: password.value
        })
        
        if (response.Success) {
          resetSuccess.value = true
          showMessage('Password reset successfully!', 'success')
        } else {
          showMessage(response.Message || 'Failed to reset password', 'error')
        }
      } catch (error) {
        console.error('Reset password error:', error)
        if (error.message.includes('Invalid or expired')) {
          showMessage('Invalid or expired reset token. Please request a new password reset.', 'error')
        } else {
          showMessage('An error occurred. Please try again.', 'error')
        }
      } finally {
        loading.value = false
      }
    }

    onMounted(() => {
      token.value = route.query.token || ''
      if (!token.value) {
        showMessage('Invalid reset link. Please request a new password reset.', 'error')
        setTimeout(() => {
          router.push('/forgot-password')
        }, 3000)
      }
    })

    return {
      password,
      confirmPassword,
      showPassword,
      showConfirmPassword,
      loading,
      message,
      messageType,
      resetSuccess,
      isFormValid,
      handleSubmit
    }
  }
}
</script>

<style scoped>
.auth-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.auth-container {
  width: 100%;
  max-width: 450px;
}

.auth-card {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
}

.auth-header {
  text-align: center;
  margin-bottom: 30px;
}

.auth-header h1 {
  color: #ffffff;
  font-size: 28px;
  font-weight: 600;
  margin-bottom: 10px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.auth-header p {
  color: #e2e8f0;
  font-size: 16px;
  line-height: 1.5;
}

.message {
  padding: 12px 16px;
  border-radius: 12px;
  margin-bottom: 20px;
  font-weight: 500;
}

.message.success {
  background: rgba(34, 197, 94, 0.2);
  border: 1px solid rgba(34, 197, 94, 0.3);
  color: #4ade80;
}

.message.error {
  background: rgba(239, 68, 68, 0.2);
  border: 1px solid rgba(239, 68, 68, 0.3);
  color: #f87171;
}

.message.info {
  background: rgba(59, 130, 246, 0.2);
  border: 1px solid rgba(59, 130, 246, 0.3);
  color: #60a5fa;
}

.auth-form {
  margin-bottom: 30px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  color: #f8fafc;
  font-weight: 500;
  margin-bottom: 8px;
  font-size: 14px;
}

.password-input {
  position: relative;
}

.form-group input {
  width: 100%;
  padding: 14px 50px 14px 16px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  color: #ffffff;
  font-size: 16px;
  transition: all 0.3s ease;
  box-sizing: border-box;
}

.form-group input::placeholder {
  color: #94a3b8;
}

.form-group input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  background: rgba(255, 255, 255, 0.15);
}

.form-group input:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.password-toggle {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  font-size: 16px;
  transition: color 0.3s ease;
  padding: 4px;
}

.password-toggle:hover:not(:disabled) {
  color: #667eea;
}

.password-toggle:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.password-hint {
  color: #94a3b8;
  font-size: 12px;
  margin-top: 4px;
  display: block;
}

.auth-button {
  width: 100%;
  padding: 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
  text-decoration: none;
}

.auth-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.6);
}

.auth-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

.loading-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.success-content {
  text-align: center;
}

.success-icon {
  font-size: 64px;
  color: #4ade80;
  margin-bottom: 20px;
}

.success-content h2 {
  color: #ffffff;
  font-size: 24px;
  margin-bottom: 16px;
}

.success-content p {
  color: #e2e8f0;
  margin-bottom: 30px;
  line-height: 1.5;
}

.auth-footer {
  text-align: center;
}

.auth-footer p {
  color: #94a3b8;
  font-size: 14px;
}

.auth-link {
  color: #667eea;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.3s ease;
}

.auth-link:hover {
  color: #764ba2;
  text-decoration: underline;
}

@media (max-width: 480px) {
  .auth-card {
    padding: 30px 20px;
  }
  
  .auth-header h1 {
    font-size: 24px;
  }
  
  .auth-header p {
    font-size: 14px;
  }

  .success-icon {
    font-size: 48px;
  }

  .success-content h2 {
    font-size: 20px;
  }
}
</style>
